import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { ProductVariantsDisplay } from 'shared-types/src/variants'
import { usePage } from '~/hooks'
import { MIN_DESKTOP_WIDTH } from '~/providers/PageProvider'
import styles from './ProductDashboard.module.css'
import { ProductDashboardProps } from './ProductDashboard.types'
import { ProductDashboardBulkPricing } from './ProductDashboardBulkPricing'
import { ProductDashboardCartControls } from './ProductDashboardCartControls'
import { ProductDashboardColorSwatch } from './ProductDashboardColorSwatch'
import { ProductDashboardStickyControls } from './ProductDashboardStickyControls'
import { ProductDashboardGallery } from './ProductDashboardGallery'
import { ProductDashboardGuarantee } from './ProductDashboardGuarantee'
import { ProductDashboardHighlights } from './ProductDashboardHighlights'
import { ProductDashboardPaymentMethods } from './ProductDashboardPaymentMethods'
import { ProductDashboardPricing } from './ProductDashboardPricing'
import { ProductDashboardShippingMethods } from './ProductDashboardShippingMethods'
import { ProductDashboardTitle } from './ProductDashboardTitle'
import { ProductDashboardMiniReviewSummary } from './ProductDashboardMiniReviewSummary'
import { ProductDashboardSummary } from './ProductDashboardSummary'
import { ProductDashboardVariantOptions } from './ProductDashboardVariantOptions'
import { ProductDashboardSpecifications } from './ProductDashboardSpecifications'
import { ProductDashboardReviews } from './ProductDashboardReviews'
import { BrazeBanner } from '../../components'
import { ProductSpecialOfferCard } from '../../components/ProductSpecialOfferCard'
import { ProductSpecialOrderCard } from '../../components/ProductSpecialOrderCard'
import { HtmlContent } from '../HtmlContent'

export const ProductDashboard = ({
  product,
  resolvedCustomerType,
  storeAvailabilityStatus,
  specialCard,
  payLaterProviders,
  productSlideVideo: rawWebContent,
  downloads,
  overview,
  specification,
  reviewData,
  bazaarvoice,
  categoryKey,
  variantsDisplay,
  colorVariants,
  colorSwatchLabel,
  colorSwatchInstructions,
  overviewHtmlContent,
  htmlContentBlock1,
}: ProductDashboardProps) => {
  const cartControlsRef = useRef<HTMLDivElement>(null)
  const { pageWidth } = usePage()

  // `pageWidth` is undefined until the page header's ResizeObserver reports in,
  // so fall back to the desktop layout — that keeps the summary and
  // specifications in the server-rendered document order.
  const isDesktop = (pageWidth ?? MIN_DESKTOP_WIDTH) >= MIN_DESKTOP_WIDTH

  const summaryAndSpecifications = (
    <>
      <ProductDashboardSummary
        downloads={downloads}
        overview={overview}
        overviewHtmlContent={overviewHtmlContent}
      />
      <ProductDashboardSpecifications specification={specification} />
    </>
  )

  const htmlContentBlocks = !!htmlContentBlock1?.length && (
    <div className={styles.pdpHtmlContentBlock}>
      {htmlContentBlock1.map((htmlContentItem, index) => {
        return (
          <HtmlContent
            key={htmlContentItem.id || index}
            contentId={htmlContentItem.id || ''}
            content={htmlContentItem.content || ''}
            css={htmlContentItem.css}
            javascript={htmlContentItem.javascript}
            isPageSection={false}
          />
        )
      })}
    </div>
  )

  useEffect(() => {
    const interval = setInterval(() => {
      const user = window.braze?.getUser()
      const placementIds = window.brazeBannerPlacementIds

      if (
        !user?.setCustomUserAttribute ||
        !window.braze?.requestContentCardsRefresh ||
        !window.braze?.requestBannersRefresh ||
        !placementIds
      ) {
        return
      }

      user.setCustomUserAttribute('pdp_category_code', categoryKey)
      window.braze.requestContentCardsRefresh(
        () => {
          console.log('Braze content cards refresh succeeded')
        },
        () => {
          console.error('Braze content cards refresh failed')
        }
      )
      window.braze.requestBannersRefresh(placementIds)

      clearInterval(interval)
    }, 250)

    return () => {
      clearInterval(interval)
    }
  }, [categoryKey])

  return (
    <>
      <ProductDashboardStickyControls
        product={product}
        observeRef={cartControlsRef}
      />
      <section className={styles.productDashboard}>
        <div className={styles.pdpGrid}>
          <div className={styles.pdpInfo}>
            <ProductDashboardHighlights product={product} />
            <ProductDashboardGallery
              product={product}
              rawWebContent={rawWebContent}
            />
            {isDesktop && htmlContentBlocks}
            {isDesktop && summaryAndSpecifications}
          </div>
          <div>
            <ProductDashboardTitle product={product} />
            <ProductDashboardMiniReviewSummary reviewData={reviewData} />
            <ProductDashboardPricing
              product={product}
              resolvedCustomerType={resolvedCustomerType}
            />
            <ProductDashboardBulkPricing product={product} />
            <ProductDashboardCartControls
              product={product}
              containerRef={cartControlsRef}
            />
            <ProductDashboardGuarantee product={product} />
            {variantsDisplay !== ProductVariantsDisplay.ColourSwatch && (
              <ProductDashboardVariantOptions
                title='Available Variants'
                variants={product.variants?.[0]?.values}
                variantsDisplay={variantsDisplay}
              />
            )}
            <ProductDashboardVariantOptions
              title='Spare Parts Available'
              variants={product.spareParts}
              skuLabel='Cat. No:'
            />
            <div className={styles.shippingPayLater}>
              <ProductDashboardShippingMethods
                product={product}
                storeAvailabilityStatus={storeAvailabilityStatus}
              />
              <ProductDashboardPaymentMethods />
            </div>
            {!isDesktop && htmlContentBlocks}
            {variantsDisplay === ProductVariantsDisplay.ColourSwatch && (
              <ProductDashboardColorSwatch
                label={colorSwatchLabel}
                instructions={colorSwatchInstructions}
                variants={colorVariants}
              />
            )}
            <BrazeBanner placementId='pdp-slot-4' />
            {specialCard && (
              <div className={styles.specialCard}>
                {specialCard.cardType === 'special-offer' && (
                  <ProductSpecialOfferCard {...specialCard} />
                )}
                {specialCard.cardType === 'special-order' && (
                  <ProductSpecialOrderCard {...specialCard} />
                )}
              </div>
            )}
            {!isDesktop && summaryAndSpecifications}
          </div>
        </div>
        <div className={classNames(styles.pdpGrid, styles.productReviews)}>
          <div className={styles.pdpInfo}>
            <ProductDashboardReviews
              sku={product.sku}
              reviewData={reviewData}
              bazaarVoice={bazaarvoice}
            />
          </div>
        </div>
        <div className={styles.pdpCustomWidgets}>
          <BrazeBanner placementId='pdp-slot-2' />
          <BrazeBanner placementId='pdp-slot-3' />
        </div>
      </section>
    </>
  )
}
